import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { ArrowLeft, Download, Info, Loader2, Pencil } from 'lucide-react';
import { toast } from '@/lib/toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import PageContainer from '@/components/layout/PageContainer';
import { ErrorReportCard } from '@/components/ErrorReportPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { STATUS_LABELS, STATUS_VARIANT } from '@/components/quotes/quoteMeta';
import { formatHours, groupQuoteHoursLines, periodTitle } from '@/components/quotes/quoteHoursView';
import api from '@/lib/api';
import { inlineError, type InlineError } from '@/lib/errorReport';
import { formatCurrencyRange } from '@/lib/format';
import { getHubSpotContext } from '@/lib/hubspotContext';
import { cn } from '@/lib/utils';
import type { QuoteHoursReport } from '@/types/quoteHours';

const NUMBER_CELL = 'px-3 py-2 text-right tabular-nums whitespace-nowrap';
const HEAD_CELL = 'px-3 py-2 text-left text-xs font-medium text-muted-foreground';

const signedDate = (value: string) =>
  new Date(value).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

function Section({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-10">
      <h2 id={id} className="text-lg font-semibold">
        {title}
      </h2>
      {description && (
        <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">{description}</p>
      )}
      <div className="mt-4 overflow-x-auto rounded-lg border bg-card">{children}</div>
    </section>
  );
}

/**
 * Welke uren zitten er achter een offerte? Voor wie na het akkoord moet
 * plannen: per uursoort, per fase en per regel, en als CSV voor Excel of een
 * planningstool. De uren komen uit de offerteregels zelf, dus dit is wat de
 * klant heeft geaccepteerd, inclusief regels die de klant niet ziet.
 */
export default function QuoteHoursPage() {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<QuoteHoursReport | null>(null);
  const [error, setError] = useState<InlineError | null>(null);
  const [downloading, setDownloading] = useState(false);
  usePageTitle('Urenoverzicht', report?.quote.title);

  const contextQuery = useMemo(() => {
    const context = getHubSpotContext();
    const query = new URLSearchParams();
    if (context.portalId) query.set('portalId', context.portalId);
    if (context.dealId) query.set('dealId', context.dealId);
    return query.size ? `?${query}` : '';
  }, []);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setReport(null);
    setError(null);
    api
      .getQuoteHours(id)
      .then((data) => {
        if (active) setReport(data);
      })
      .catch((err: AxiosError) => {
        if (!active) return;
        setError(
          inlineError(
            err.response?.status === 404
              ? 'Deze offerte bestaat niet of hoort bij een ander HubSpot-portaal.'
              : 'Het urenoverzicht kon niet worden geladen. Probeer het opnieuw.',
            err
          )
        );
      });
    return () => {
      active = false;
    };
  }, [id]);

  const phases = useMemo(
    () => groupQuoteHoursLines(report?.lines.filter((line) => line.included) ?? []),
    [report]
  );
  const notIncluded = report?.lines.filter((line) => !line.included) ?? [];
  const showPeriods =
    (report?.totals.length ?? 0) > 1 || report?.totals[0]?.billingPeriod !== 'one_time';
  const amount = (cents: number | null, maxCents?: number) =>
    cents === null
      ? '—'
      : formatCurrencyRange(
          cents / 100,
          maxCents === undefined ? null : maxCents / 100,
          report?.quote.currency
        );

  const handleExport = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      await api.downloadQuoteHours(report.quote.id, report.exportFilename);
    } catch (err) {
      toast.error('Exporteren mislukt', { error: err });
    } finally {
      setDownloading(false);
    }
  };

  const backLink = (
    <Link
      to={`/quotes${contextQuery}`}
      className="mb-3 inline-flex items-center gap-1.5 rounded text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      Terug naar offertes
    </Link>
  );

  if (error) {
    return (
      <PageContainer>
        {backLink}
        <h1 className="text-2xl font-bold">Urenoverzicht</h1>
        <p className="mt-4 text-sm text-destructive" role="alert">
          {error.message}
        </p>
        {error.report && <ErrorReportCard report={error.report} className="mt-4" />}
      </PageContainer>
    );
  }

  if (!report) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">Urenoverzicht laden…</span>
      </div>
    );
  }

  const { quote } = report;
  const status = quote.status.toUpperCase();
  const accepted = status === 'ACCEPTED';
  const meta = [quote.companyName, quote.quoteNumber].filter(Boolean).join(' · ');
  const hasHours = report.lines.length > 0;

  return (
    <PageContainer>
      <header className="flex flex-wrap items-end justify-between gap-4 border-b pb-5">
        <div className="min-w-0">
          {backLink}
          <h1 className="text-2xl font-bold">Urenoverzicht</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{quote.title}</span>
            {meta && ` · ${meta}`}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={STATUS_VARIANT[status] ?? 'secondary'}>
              {STATUS_LABELS[status] ?? quote.status}
            </Badge>
            {quote.signedAt && <span>Getekend op {signedDate(quote.signedAt)}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to={`/quotes/${quote.id}/edit`}>
              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
              Offerte openen
            </Link>
          </Button>
          <Button onClick={handleExport} disabled={!hasHours || downloading}>
            {downloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Exporteren (CSV)
          </Button>
        </div>
      </header>

      {!accepted && (
        <p className="mt-5 flex items-start gap-2 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Deze offerte is nog niet geaccepteerd. De uren kunnen nog veranderen zolang de offerte
          wordt bewerkt.
        </p>
      )}

      {!hasHours ? (
        <div className="mt-10 rounded-lg border border-dashed p-8 text-center">
          <p className="font-medium">Er staan geen uren in deze offerte</p>
          <p className="mx-auto mt-1 max-w-[60ch] text-sm text-muted-foreground">
            Uren komen uit producten met een urenopbouw en uit uurregels in de investering. Een
            vaste prijs zonder uren staat hieronder, als die er is.
          </p>
        </div>
      ) : (
        <>
          <dl className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {report.totals.map((total) => (
              <div key={total.billingPeriod} className="rounded-lg border bg-card p-4">
                <dt className="text-xs font-medium text-muted-foreground">
                  {periodTitle(total.billingPeriod)}
                </dt>
                <dd className="mt-1 text-2xl font-semibold tabular-nums">
                  {formatHours(total.hours, total.maxHours)}
                </dd>
                <dd className="text-sm text-muted-foreground tabular-nums">
                  {amount(total.amountCents, total.maxAmountCents)}
                </dd>
              </div>
            ))}
          </dl>

          <Section
            id="hours-by-rate"
            title="Per uursoort"
            description="Opgeteld over alle fases, met het aantal en de korting meegerekend."
          >
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className={HEAD_CELL}>Uursoort</th>
                  <th className={cn(HEAD_CELL, 'hidden sm:table-cell')}>Discipline</th>
                  {showPeriods && (
                    <th className={cn(HEAD_CELL, 'hidden sm:table-cell')}>Facturatie</th>
                  )}
                  <th className={cn(HEAD_CELL, 'text-right')}>Uren</th>
                  <th className={cn(HEAD_CELL, 'text-right')}>Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {report.byRate.map((rate) => (
                  <tr key={`${rate.billingPeriod}:${rate.rateKey ?? rate.rateName}`}>
                    <td className="px-3 py-2 font-medium">{rate.rateName}</td>
                    <td className="hidden px-3 py-2 text-muted-foreground sm:table-cell">
                      {rate.rateGroup ?? (rate.rateKey ? '' : 'Geen tarief gekozen in de offerte')}
                    </td>
                    {showPeriods && (
                      <td className="hidden px-3 py-2 text-muted-foreground sm:table-cell">
                        {periodTitle(rate.billingPeriod)}
                      </td>
                    )}
                    <td className={NUMBER_CELL}>{formatHours(rate.hours, rate.maxHours)}</td>
                    <td className={NUMBER_CELL}>{amount(rate.amountCents, rate.maxAmountCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section
            id="hours-by-phase"
            title="Per fase"
            description="Elke regel zoals hij in de offerte staat. Interne regels tellen mee, maar ziet de klant niet."
          >
            <table className="w-full min-w-[720px] table-fixed text-sm">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[28%]" />
                <col className="w-[20%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead className="border-b">
                <tr>
                  <th className={HEAD_CELL}>Onderdeel</th>
                  <th className={HEAD_CELL}>Werkzaamheid</th>
                  <th className={HEAD_CELL}>Uursoort</th>
                  <th className={cn(HEAD_CELL, 'text-right')}>Uren</th>
                  <th className={cn(HEAD_CELL, 'text-right')}>Tarief</th>
                  <th className={cn(HEAD_CELL, 'text-right')}>Bedrag</th>
                </tr>
              </thead>
              {phases.map((phase, phaseIndex) => (
                <tbody key={`${phase.phase}-${phaseIndex}`} className="border-b last:border-b-0">
                  <tr className="bg-muted/40">
                    <th colSpan={3} scope="rowgroup" className="px-3 py-2 text-left font-semibold">
                      {phase.phase}
                    </th>
                    <td className={cn(NUMBER_CELL, 'font-semibold')}>
                      {formatHours(phase.hours, phase.maxHours)}
                    </td>
                    <td />
                    <td className={cn(NUMBER_CELL, 'font-semibold')}>
                      {amount(phase.amountCents, phase.maxAmountCents)}
                    </td>
                  </tr>
                  {phase.items.flatMap((group) =>
                    group.lines.map((line, index) => (
                      <tr key={`${group.item}-${index}-${line.description}`} className="align-top">
                        <td className="break-words px-3 py-2">
                          {index === 0 && (
                            <>
                              <span className="font-medium">{group.item}</span>
                              {group.variant && (
                                <span className="text-muted-foreground"> · {group.variant}</span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="break-words px-3 py-2">
                          {/* Een losse uurregel heeft geen aparte werkzaamheid: dan staat de naam al links. */}
                          {(group.lines.length > 1 || line.description !== group.item) &&
                            line.description}
                          {line.internal && (
                            <Badge
                              variant="outline"
                              className="ml-2 align-middle text-[10px]"
                              title="Telt mee, maar staat niet op de offerte"
                            >
                              Intern
                            </Badge>
                          )}
                        </td>
                        <td className="break-words px-3 py-2 text-muted-foreground">
                          {line.rateName}
                        </td>
                        <td className={NUMBER_CELL}>{formatHours(line.hours, line.maxHours)}</td>
                        <td className={cn(NUMBER_CELL, 'text-muted-foreground')}>
                          {amount(line.rateCents)}
                        </td>
                        <td className={NUMBER_CELL}>
                          {amount(line.amountCents, line.maxAmountCents)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              ))}
            </table>
          </Section>
        </>
      )}

      {report.itemsWithoutHours.length > 0 && (
        <Section
          id="hours-without-breakdown"
          title="Zonder urenopbouw"
          description="Onderdelen met een bedrag maar zonder uren in de offerte, bijvoorbeeld een vaste prijs. Ze staan niet in de export."
        >
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className={HEAD_CELL}>Fase</th>
                <th className={HEAD_CELL}>Onderdeel</th>
                <th className={cn(HEAD_CELL, 'text-right')}>Bedrag</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {report.itemsWithoutHours.map((item, index) => (
                <tr
                  key={`${item.phase}-${item.item}-${index}`}
                  className={cn(!item.included && 'text-muted-foreground')}
                >
                  <td className="px-3 py-2 text-muted-foreground">{item.phase}</td>
                  <td className="px-3 py-2">
                    {item.item}
                    {!item.included && (
                      <span className="ml-2 text-xs">(optie, niet aangevinkt)</span>
                    )}
                  </td>
                  <td className={NUMBER_CELL}>
                    {item.amountCents === null
                      ? (item.priceText ?? '—')
                      : amount(item.amountCents, item.maxAmountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {notIncluded.length > 0 && (
        <Section
          id="hours-not-included"
          title="Niet meegerekend"
          description="Optionele regels die niet zijn aangevinkt. Ze tellen niet mee in de totalen en staan niet in de export."
        >
          <table className="w-full text-sm text-muted-foreground">
            <thead className="border-b">
              <tr>
                <th className={HEAD_CELL}>Fase</th>
                <th className={HEAD_CELL}>Onderdeel</th>
                <th className={HEAD_CELL}>Werkzaamheid</th>
                <th className={cn(HEAD_CELL, 'text-right')}>Uren</th>
                <th className={cn(HEAD_CELL, 'text-right')}>Bedrag</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {notIncluded.map((line, index) => (
                <tr key={`${line.phase}-${line.item}-${index}`}>
                  <td className="px-3 py-2">{line.phase}</td>
                  <td className="px-3 py-2">{line.item}</td>
                  <td className="px-3 py-2">{line.description}</td>
                  <td className={NUMBER_CELL}>{formatHours(line.hours, line.maxHours)}</td>
                  <td className={NUMBER_CELL}>{amount(line.amountCents, line.maxAmountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </PageContainer>
  );
}
